import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  listAllProviders,
  saveProvider,
  deleteProvider,
  decideProvider,
  clearPending,
  importProviders,
  fetchCandidates,
  CatalogProvider,
  ProviderInput,
  FeedStatus,
  RiskLevel,
} from '../lib/providerCatalogApi';
import { useTranslation } from 'react-i18next';

interface ProviderCatalogProps {
  apiUrl: string;
}

const RISK_BADGE: Record<string, string> = {
  sanctioned: 'badge-success',
  tolerated: 'badge-info',
  unsanctioned: 'badge-unknown',
  high_risk: 'badge-error',
};
const RISK_LEVELS: RiskLevel[] = ['sanctioned', 'tolerated', 'unsanctioned', 'high_risk'];

const SOURCE_BADGE: Record<string, string> = {
  bundled: 'badge-redact',
  manual: 'badge-info',
  import: 'badge-info',
};

const EMPTY_FORM: ProviderInput = { id: '', label: '', kind: 'cloud', type: 'llm', riskLevel: 'unsanctioned', match: [], apps: [] };

// match[]/apps[] are entered as comma- or space-separated tokens.
const toTokens = (s: string): string[] =>
  s.split(/[\s,]+/).map((t) => t.trim()).filter(Boolean);

const ProviderCatalog: React.FC<ProviderCatalogProps> = ({ apiUrl }) => {
  const { t } = useTranslation();
  const [providers, setProviders] = useState<CatalogProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState<{ kind: string; text: string } | null>(null);

  // Add/edit modal
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<ProviderInput>(EMPTY_FORM);
  const [matchText, setMatchText] = useState('');
  const [appsText, setAppsText] = useState('');
  const [editingBase, setEditingBase] = useState(false);
  const [saving, setSaving] = useState(false);

  // Import
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState('');

  // Fetch
  const [fetching, setFetching] = useState(false);
  const [feeds, setFeeds] = useState<FeedStatus[] | null>(null);

  const load = useCallback(async () => {
    try {
      setProviders(await listAllProviders(apiUrl));
    } catch (err: any) {
      setNotice({ kind: 'error', text: err.message || 'Failed to load catalogue' });
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    load();
  }, [load]);

  const active = useMemo(() => providers.filter((p) => p.status !== 'pending' && p.status !== 'rejected'), [providers]);
  const pending = useMemo(() => providers.filter((p) => p.status === 'pending'), [providers]);

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setMatchText('');
    setAppsText('');
    setEditingBase(false);
    setShowForm(true);
  };

  const openEdit = (p: CatalogProvider) => {
    setForm({
      id: p.id,
      label: p.label,
      kind: p.kind || 'cloud',
      type: p.type || 'llm',
      riskLevel: (p.riskLevel as RiskLevel) || 'unsanctioned',
      match: p.match || [],
      apps: p.apps || [],
    });
    setMatchText((p.match || []).join(', '));
    setAppsText((p.apps || []).join(', '));
    setEditingBase(p.source === 'bundled');
    setShowForm(true);
  };

  const submitForm = async () => {
    const input: ProviderInput = {
      ...form,
      match: toTokens(matchText),
      apps: toTokens(appsText),
    };
    if (!input.id.trim() || !input.label.trim() || input.match.length === 0) {
      setNotice({ kind: 'error', text: t('pages.providerCatalog.requiredFields') });
      return;
    }
    setSaving(true);
    try {
      await saveProvider(apiUrl, input);
      setShowForm(false);
      setNotice({ kind: 'success', text: `Saved "${input.label}".` });
      await load();
    } catch (err: any) {
      setNotice({ kind: 'error', text: err.message || 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (p: CatalogProvider) => {
    try {
      await deleteProvider(apiUrl, p.id);
      setNotice({ kind: 'success', text: `Removed "${p.label}".` });
      await load();
    } catch (err: any) {
      setNotice({ kind: 'error', text: err.message || 'Delete failed' });
    }
  };

  const onDecision = async (p: CatalogProvider, decision: 'approved' | 'rejected' | 'disabled' | 'active') => {
    try {
      await decideProvider(apiUrl, p.id, decision);
      await load();
    } catch (err: any) {
      setNotice({ kind: 'error', text: err.message || 'Action failed' });
    }
  };

  // Clear every pending candidate at once. 'reject' tombstones them (won't be
  // re-fetched); 'delete' just unstages them (may re-appear on the next fetch).
  const onClearPending = async (mode: 'reject' | 'delete') => {
    const verb = mode === 'reject' ? 'Reject' : 'Dismiss';
    if (!window.confirm(`${verb} all ${pending.length} pending candidate(s)?`)) return;
    try {
      const res = await clearPending(apiUrl, mode);
      setNotice({ kind: 'success', text: `${verb}ed ${res.cleared} candidate(s).` });
      await load();
    } catch (err: any) {
      setNotice({ kind: 'error', text: err.message || 'Clear failed' });
    }
  };

  const runImport = async () => {
    let payload: any;
    try {
      payload = JSON.parse(importText);
    } catch {
      setNotice({ kind: 'error', text: 'Invalid JSON — paste an array of providers or { "providers": [...] }.' });
      return;
    }
    try {
      const res = await importProviders(apiUrl, payload);
      setShowImport(false);
      setImportText('');
      const errs = res.errors.length ? ` (${res.errors.length} skipped)` : '';
      setNotice({ kind: 'success', text: `Imported ${res.imported} provider(s)${errs}.` });
      await load();
    } catch (err: any) {
      setNotice({ kind: 'error', text: err.message || 'Import failed' });
    }
  };

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setImportText(String(reader.result || ''));
    reader.readAsText(file);
  };

  const runFetch = async () => {
    setFetching(true);
    setFeeds(null);
    try {
      const res = await fetchCandidates(apiUrl);
      setFeeds(res.feeds);
      setNotice({
        kind: res.staged ? 'success' : 'info',
        text: `Fetched feeds — ${res.staged} new candidate(s) staged, ${res.skipped} already known.`,
      });
      await load();
    } catch (err: any) {
      setNotice({ kind: 'error', text: err.message || 'Fetch failed' });
    } finally {
      setFetching(false);
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <div className="ent-empty">{t('pages.providerCatalog.loading')}</div>
      </div>
    );
  }

  const isBundled = (p: CatalogProvider) => p.source === 'bundled';

  return (
    <div className="page-content">
      <div className="page-title">
        <div>
          <h1>{t('pages.providerCatalog.providerCatalog')}</h1>
          <p className="subtitle">
            {t('pages.providerCatalog.extendTheShadowAIProviderCatalogue')}
          </p>
        </div>
      </div>

      {notice && (
        <div className={`notice ${notice.kind}`} style={{ marginBottom: 12 }}>
          {notice.text}
        </div>
      )}

      <div className="ent-card" style={{ marginBottom: 16 }}>
        <div className="ent-card-body">
          <div className="ent-toolbar" style={{ marginTop: 0 }}>
            <button className="ent-btn primary" onClick={openAdd}>{t('pages.providerCatalog.addProvider')}</button>
            <button className="ent-btn" onClick={() => setShowImport((v) => !v)}>{t('pages.providerCatalog.importJSON')}</button>
            <button className="ent-btn" onClick={runFetch} disabled={fetching} style={{ marginLeft: 'auto' }}>
              {fetching ? t('pages.providerCatalog.fetching') : t('pages.providerCatalog.checkForNewProviders')}
            </button>
          </div>

          {showImport && (
            <div style={{ marginTop: 12 }}>
              <textarea
                className="ent-textarea"
                rows={6}
                placeholder={t('pages.providerCatalog.idAcmeLabelAcmeAIRiskLevel')}
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
              />
              <div className="ent-toolbar">
                <input type="file" accept="application/json,.json" onChange={onFile} />
                <button className="ent-btn primary" onClick={runImport} disabled={!importText.trim()} style={{ marginLeft: 'auto' }}>
                  {t('pages.providerCatalog.import')}
                </button>
              </div>
            </div>
          )}

          {feeds && (
            <div className="notice info" style={{ marginTop: 12 }}>
              {feeds.map((f) => (
                <span key={f.feed} style={{ marginRight: 12 }}>
                  <span className={`badge ${f.ok ? 'badge-success' : 'badge-error'}`}>{f.feed}</span>{' '}
                  {f.ok ? t('pages.providerCatalog.providersCount', { count: f.count }) : f.error || t('pages.providerCatalog.failed')}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {pending.length > 0 && (
        <div className="ent-card" style={{ marginBottom: 16 }}>
          <div className="ent-card-head">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <span>{t('pages.providerCatalog.pendingCandidatesReviewBeforeTheyTake', { v0: pending.length })}</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="ent-btn sm" onClick={() => onClearPending('reject')} title={t('pages.providerCatalog.tombstoneAllTheyWontBeSuggested')}>
                  {t('pages.providerCatalog.rejectAll')}
                </button>
                <button className="ent-btn sm" onClick={() => onClearPending('delete')} title={t('pages.providerCatalog.unstageAllRemovedNowButMay')}>
                  {t('pages.providerCatalog.dismissAll')}
                </button>
              </div>
            </div>
          </div>
          <div className="ent-card-body">
            <div className="ent-table-wrap">
              <table className="ent-table">
                <thead>
                  <tr>
                    <th>{t('pages.providerCatalog.provider')}</th>
                    <th style={{ width: 110 }}>{t('pages.providerCatalog.risk')}</th>
                    <th>{t('pages.providerCatalog.match')}</th>
                    <th style={{ width: 140 }}>{t('pages.providerCatalog.source')}</th>
                    <th style={{ width: 250 }}>{t('pages.providerCatalog.decision')}</th>
                  </tr>
                </thead>
                <tbody>
                  {pending.map((p) => (
                    <tr key={p.id}>
                      <td><strong>{p.label}</strong><div style={{ color: 'var(--text-2)', fontSize: 11 }}>{p.id}</div></td>
                      <td>{p.riskLevel && <span className={`badge ${RISK_BADGE[p.riskLevel]}`}>{p.riskLevel}</span>}</td>
                      <td style={{ color: 'var(--text-2)', fontSize: 12 }}>{p.match.join(', ')}</td>
                      <td style={{ color: 'var(--text-2)', fontSize: 12 }}>{p.source}</td>
                      <td>
                        <button className="ent-btn sm primary" onClick={() => onDecision(p, 'approved')}>{t('pages.providerCatalog.approve')}</button>{' '}
                        <button className="ent-btn sm" onClick={() => onDecision(p, 'rejected')} title={t('pages.providerCatalog.wontBeSuggestedAgain')}>{t('pages.providerCatalog.reject')}</button>{' '}
                        <button className="ent-btn sm ghost" onClick={() => onDelete(p)} title={t('pages.providerCatalog.unstageMayReAppearOnNext')}>{t('pages.providerCatalog.dismiss')}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <div className="ent-card">
        <div className="ent-card-head">{t('pages.providerCatalog.catalogue', { v0: active.length })}</div>
        <div className="ent-card-body">
          <div className="ent-table-wrap scroll">
            <table className="ent-table">
              <thead>
                <tr>
                  <th>{t('pages.providerCatalog.provider')}</th>
                  <th style={{ width: 80 }}>{t('pages.providerCatalog.type')}</th>
                  <th style={{ width: 110 }}>{t('pages.providerCatalog.risk')}</th>
                  <th>{t('pages.providerCatalog.match')}</th>
                  <th style={{ width: 100 }}>{t('pages.providerCatalog.source')}</th>
                  <th style={{ width: 200 }}>{t('pages.providerCatalog.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {active.length === 0 && <tr><td colSpan={6} className="ent-empty">{t('pages.providerCatalog.noProviders')}</td></tr>}
                {active.map((p) => {
                  const disabled = p.status === 'disabled';
                  return (
                    <tr key={p.id} style={disabled ? { opacity: 0.5 } : undefined}>
                      <td>
                        <strong>{p.label}</strong>
                        {disabled && <span className="badge badge-unknown" style={{ marginLeft: 6, fontSize: 10 }}>{t('pages.providerCatalog.disabled')}</span>}
                        <div style={{ color: 'var(--text-2)', fontSize: 11 }}>{p.id}</div>
                      </td>
                      <td><span className={`badge ${p.type === 'mcp' ? 'badge-info' : 'badge-redact'}`}>{p.type || t('pages.providerCatalog.llm')}</span></td>
                      <td>{p.riskLevel && <span className={`badge ${RISK_BADGE[p.riskLevel]}`}>{p.riskLevel}</span>}</td>
                      <td style={{ color: 'var(--text-2)', fontSize: 12 }}>
                        {p.match.join(', ')}
                        {p.apps && p.apps.length > 0 && (
                          <div style={{ marginTop: 2 }}>
                            <span className="badge badge-error" style={{ fontSize: 10 }}>{t('pages.providerCatalog.terminates')} {p.apps.join(', ')}</span>
                          </div>
                        )}
                      </td>
                      <td><span className={`badge ${SOURCE_BADGE[p.source || ''] || 'badge-unknown'}`}>{p.source || t('pages.providerCatalog.custom')}</span></td>
                      <td>
                        <button className="ent-btn sm" onClick={() => openEdit(p)}>{t('pages.providerCatalog.edit')}</button>{' '}
                        {isBundled(p) ? (
                          <button className="ent-btn sm" onClick={() => onDecision(p, 'disabled')}>{t('pages.providerCatalog.disable')}</button>
                        ) : disabled ? (
                          <button className="ent-btn sm" onClick={() => onDelete(p)}>{t('pages.providerCatalog.enable')}</button>
                        ) : (
                          <button className="ent-btn sm" onClick={() => onDelete(p)}>{t('pages.providerCatalog.remove')}</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showForm && (
        <>
          <div className="ent-overlay" onClick={() => setShowForm(false)} />
          <div className="ent-modal" onClick={() => setShowForm(false)}>
            <div className="ent-modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
              <div className="ent-modal-head">
                <h2>{editingBase ? t('pages.providerCatalog.overrideBundledProvider') : form.id ? t('pages.providerCatalog.editProvider') : t('pages.providerCatalog.addProvider2')}</h2>
              </div>
              <div className="ent-modal-body">
                {editingBase && (
                  <div className="notice info" style={{ marginTop: 0, marginBottom: 12 }}>
                    {t('pages.providerCatalog.savingCreatesAnOverlayEntryThat')}
                  </div>
                )}
                <div className="ent-field">
                  <label>{t('pages.providerCatalog.id')}</label>
                  <input
                    className="ent-input"
                    value={form.id}
                    disabled={!!form.id && (editingBase || providers.some((p) => p.id === form.id && p.source !== 'bundled'))}
                    onChange={(e) => setForm({ ...form, id: e.target.value })}
                    placeholder={t('pages.providerCatalog.acmeAi')}
                  />
                </div>
                <div className="ent-field">
                  <label>{t('pages.providerCatalog.label')}</label>
                  <input className="ent-input" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder={t('pages.providerCatalog.acmeAI')} />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <div className="ent-field" style={{ flex: 1 }}>
                    <label>{t('pages.providerCatalog.kind')}</label>
                    <select className="ent-input" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
                      <option value="cloud">{t('pages.providerCatalog.cloud')}</option>
                      <option value="embedded">{t('pages.providerCatalog.embedded')}</option>
                    </select>
                  </div>
                  <div className="ent-field" style={{ flex: 1 }}>
                    <label>{t('pages.providerCatalog.type')}</label>
                    <select className="ent-input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as 'llm' | 'mcp' })}>
                      <option value="llm">{t('pages.providerCatalog.llm')}</option>
                      <option value="mcp">{t('pages.providerCatalog.mcp')}</option>
                    </select>
                  </div>
                  <div className="ent-field" style={{ flex: 1 }}>
                    <label>{t('pages.providerCatalog.risk')}</label>
                    <select className="ent-input" value={form.riskLevel} onChange={(e) => setForm({ ...form, riskLevel: e.target.value as RiskLevel })}>
                      {RISK_LEVELS.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                </div>
                <div className="ent-field">
                  <label>{t('pages.providerCatalog.matchDomainsTokensCommaOrSpace')}</label>
                  <input className="ent-input" value={matchText} onChange={(e) => setMatchText(e.target.value)} placeholder={t('pages.providerCatalog.acmeLlmComApiAcmeAi')} />
                </div>
                <div className="ent-field">
                  <label>{t('pages.providerCatalog.terminateProcessesOptionalDesktopClients')}</label>
                  <input className="ent-input" value={appsText} onChange={(e) => setAppsText(e.target.value)} placeholder={t('pages.providerCatalog.acmedesktop')} />
                </div>
              </div>
              <div className="ent-modal-foot">
                <button className="ent-btn ghost" onClick={() => setShowForm(false)}>{t('pages.providerCatalog.cancel')}</button>
                <button className="ent-btn primary" onClick={submitForm} disabled={saving}>
                  {saving ? t('pages.providerCatalog.saving') : t('pages.providerCatalog.save')}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProviderCatalog;
